import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Target, Activity, CheckCircle, Rocket, EyeOff, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import heroImage from '../assets/hero-football.jpg';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface Team {
  id: string;
  name: string;
  ageGroup: string;
}

interface Task {
  task_id: string;
  task_type: 'mandatory' | 'optional';
  task_status: 'pending' | 'completed' | 'ignored' | 'snoozed';
  task_priority: number;
  task_title: string;
  task_description: string;
  created_at: string;
  due_date?: string;
  is_overdue?: boolean;
  metadata?: {
    workflow_step?: string;
    help_url?: string;
    player_id?: string;
    player_name?: string;
    team_id?: string;
    assessment_type?: 'initial' | 'follow_up';
    due_date?: string;
    reminder_date?: string;
    interval_weeks?: number;
  };
}

const TASK_ROUTES: Record<string, string> = {
  onboarding_create_team: '/team/create',
  onboarding_add_players: '/players',
  onboarding_create_assessments: '/assess',
  setup_add_co_coach: '/add-coach',
  setup_add_training_sessions: '/teams',
  setup_set_formation: '/teams',
};

function getTaskRoute(task: Task, currentTeam?: Team): string {
  if (TASK_ROUTES[task.task_id]) {
    if (task.task_id === 'setup_add_co_coach' && currentTeam) {
      return `/teams/${currentTeam.id}/add-coach`;
    }
    return TASK_ROUTES[task.task_id];
  }
  if (task.metadata?.player_id) {
    return `/assess/${task.metadata.player_id}`;
  }
  return '/';
}

interface CornerScore {
  name: string;
  score: number;
  color: string;
}

function cornerAverage(scores: Record<string, number | undefined>): number {
  const vals = Object.values(scores).filter((v): v is number => v !== undefined);
  if (vals.length === 0) return 0;
  return vals.reduce((sum, v) => sum + v, 0) / vals.length;
}

function AssessHintOverlay({ onDismiss }: { onDismiss: () => void }) {
  const [pos, setPos] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useEffect(() => {
    // Find the Assess link in the sidebar by its href
    const link = document.querySelector('a[href="/assess"]') as HTMLElement | null;
    if (link) {
      const rect = link.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    }
  }, []);

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/40" onClick={onDismiss} />
      {/* Spotlight on the Assess sidebar item */}
      {pos && (
        <div className="hidden lg:block fixed z-[51] animate-pulse"
          style={{ top: pos.top - 2, left: pos.left - 2, width: pos.width + 4, height: pos.height + 4 }}>
          <div className="h-full bg-green-100 border-2 border-green-500 rounded-lg" />
        </div>
      )}
      {/* Tooltip */}
      {pos ? (
        <div className="hidden lg:block fixed z-[52]" style={{ top: pos.top - 10, left: pos.left + pos.width + 12 }}>
          <div className="absolute -left-2 top-5 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-white" />
          <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
            <button onClick={onDismiss} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Complete Remaining Assessments</h3>
            <p className="text-sm text-gray-600 mb-4">
              Click on the <span className="font-semibold text-green-700">Assess</span> menu item to continue assessing your remaining players and build complete baselines for your squad.
            </p>
            <button onClick={onDismiss}
              className="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
              Got it!
            </button>
          </div>
        </div>
      ) : null}
      {/* Mobile fallback */}
      <div className="lg:hidden fixed inset-x-4 top-1/3 z-[52]">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <button onClick={onDismiss} className="absolute top-3 right-7 text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Complete Remaining Assessments</h3>
          <p className="text-sm text-gray-600 mb-4">
            Tap <span className="font-semibold text-green-700">Assess</span> in the bottom navigation to continue assessing your remaining players.
          </p>
          <button onClick={onDismiss}
            className="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

export function NewDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAssessHint, setShowAssessHint] = useState(false);
  const [developmentAreas, setDevelopmentAreas] = useState<CornerScore[]>([
    { name: 'Technical', score: 0, color: 'bg-red-500' },
    { name: 'Physical', score: 0, color: 'bg-green-500' },
    { name: 'Psychological', score: 0, color: 'bg-blue-500' },
    { name: 'Social', score: 0, color: 'bg-yellow-500' },
  ]);
  const [assessmentCount, setAssessmentCount] = useState(0);

  useEffect(() => {
    // Check if we should show the assess hint lightbox
    if (localStorage.getItem('gamepath_show_assess_hint') === 'true') {
      setShowAssessHint(true);
      localStorage.removeItem('gamepath_show_assess_hint');
    }
    const fetchData = async () => {
      try {
        const [teamsRes, tasksRes] = await Promise.all([
          api.get('/teams'),
          api.get('/tasks').catch((err) => {
            console.error('Failed to fetch tasks:', err?.response?.status, err?.response?.data || err.message);
            return null;
          }),
        ]);
        const teamsData: Team[] = teamsRes.data.data || [];
        setTeams(teamsData);

        if (tasksRes) {
          const allTasks: Task[] = tasksRes.data.data || [];
          const pending = allTasks.filter(t => t.task_status === 'pending');
          setTasks(pending);
          const meta = tasksRes.data.meta;
          setPendingCount((meta?.mandatory_pending || 0) + (meta?.optional_pending || 0));
        }

        const allAssessments: any[] = [];
        // Fetch all players in parallel across teams
        // Use individual try/catch to prevent 401 interceptor from triggering on background fetches
        const playersResults = await Promise.all(
          teamsData.map(team =>
            api.get(`/teams/${team.id}/players`).then(res => res.data.data || []).catch(() => [])
          )
        );
        const allPlayers = playersResults.flat();

        // Fetch assessments in parallel (batch of 5 to avoid overwhelming the API)
        const batchSize = 5;
        for (let i = 0; i < allPlayers.length; i += batchSize) {
          const batch = allPlayers.slice(i, i + batchSize);
          const results = await Promise.all(
            batch.map((player: any) =>
              api.get(`/assessments/player/${player.id}`).then(res => res.data.data || []).catch(() => [])
            )
          );
          for (const assessments of results) {
            allAssessments.push(...assessments);
          }
        }

        setAssessmentCount(allAssessments.length);

        if (allAssessments.length > 0) {
          let techTotal = 0, physTotal = 0, psychTotal = 0, socialTotal = 0;
          for (const a of allAssessments) {
            if (a.scores) {
              techTotal += cornerAverage(a.scores.technical || {});
              physTotal += cornerAverage(a.scores.physical || {});
              psychTotal += cornerAverage(a.scores.psychological || {});
              socialTotal += cornerAverage(a.scores.social || {});
            }
          }
          const count = allAssessments.length;
          setDevelopmentAreas([
            { name: 'Technical', score: parseFloat((techTotal / count).toFixed(1)), color: 'bg-red-500' },
            { name: 'Physical', score: parseFloat((physTotal / count).toFixed(1)), color: 'bg-green-500' },
            { name: 'Psychological', score: parseFloat((psychTotal / count).toFixed(1)), color: 'bg-blue-500' },
            { name: 'Social', score: parseFloat((socialTotal / count).toFixed(1)), color: 'bg-yellow-500' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Redirect to onboarding if coach has no teams (new user)
  useEffect(() => {
    if (!loading && teams.length === 0 && user?.role === 'COACH') {
      navigate('/onboarding', { replace: true });
    }
  }, [loading, teams, user, navigate]);

  if (loading) {
    return (
      <div className="pb-4 lg:pb-8">
        <div className="bg-green-600 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto animate-pulse">
            <div className="h-8 bg-green-500 rounded w-1/3 mb-3"></div>
            <div className="h-5 bg-green-500 rounded w-1/5 mb-6"></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-green-500 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentTeam = teams[0];

  return (
    <div className="pb-4 lg:pb-8">
      {/* Assess hint lightbox for coaches who skipped remaining assessments */}
      {showAssessHint && <AssessHintOverlay onDismiss={() => setShowAssessHint(false)} />}
      {/* Hero Banner */}
      <div className="relative bg-green-600 overflow-hidden mb-6">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(100%)',
          }}
        />
        <div className="relative p-4 lg:p-8 max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
              Welcome back, {user?.profile.firstName}
            </h1>
            <p className="text-green-50">
              {currentTeam ? `${currentTeam.name} • ${currentTeam.ageGroup}` : 'No team selected'}
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Link
              to="/assess"
              className="group relative bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-white">Assess Players</span>
              </div>
            </Link>
            <Link
              to="/develop"
              className="group relative bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-6 h-6 text-yellow-600" />
                </div>
                <span className="text-sm font-semibold text-white">Development Cycle</span>
              </div>
            </Link>
            <Link
              to="/reports"
              className="group relative bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-sm font-semibold text-white">View Reports</span>
              </div>
            </Link>
            <Link
              to="/players"
              className="group relative bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                  <Activity className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-sm font-semibold text-white">Team Insights</span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-8 max-w-7xl mx-auto">
        {/* Team Development Snapshot */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Development Snapshot</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {developmentAreas.map((area) => (
              <div key={area.name} className="text-center">
                <div className="mb-2">
                  <div className={`w-16 h-16 mx-auto rounded-full ${area.color} flex items-center justify-center`}>
                    <span className="text-2xl font-bold text-white">{area.score}</span>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-900">{area.name}</p>
                <p className="text-xs text-gray-500">out of 10</p>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Based on {assessmentCount} assessment{assessmentCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Development Priorities */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Development Priorities</h2>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <Rocket className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-1">Coming Soon</p>
              <p className="text-xs text-gray-400 max-w-[200px]">
                AI-driven priorities based on your assessment data
              </p>
            </div>
          </div>

          {/* Current Training Focus */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Training Focus</h2>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <Rocket className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-1">Coming Soon</p>
              <p className="text-xs text-gray-400 max-w-[200px]">
                Personalised training plans tailored to your team
              </p>
            </div>
          </div>
        </div>

        {/* Your Tasks */}
        {tasks.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Your Tasks</h2>
              <span className="text-sm text-gray-600">{pendingCount} pending</span>
            </div>
            <div className="space-y-3">
              {tasks.map((task) => {
                const route = getTaskRoute(task, currentTeam);
                const isMandatory = task.task_type === 'mandatory';
                const isOverdue = task.is_overdue;
                const dueDate = task.due_date || task.metadata?.due_date;
                return (
                  <div key={task.task_id} className={`rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                    <Link
                      to={route}
                      className="flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors rounded-lg group"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isOverdue ? 'bg-red-100' : 'bg-gray-100'}`}>
                        {isOverdue ? (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        ) : (
                          <CheckCircle className={`w-5 h-5 ${isMandatory ? 'text-green-600' : 'text-gray-400'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 truncate">{task.task_title}</span>
                          {isOverdue && (
                            <span className="text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">Overdue</span>
                          )}
                          {!isMandatory && !isOverdue && (
                            <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">Optional</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{task.task_description}</p>
                        {dueDate && !isOverdue && (
                          <p className="text-[10px] text-gray-400 mt-0.5">Due {new Date(dueDate).toLocaleDateString()}</p>
                        )}
                        {dueDate && isOverdue && (
                          <p className="text-[10px] text-red-500 mt-0.5">Was due {new Date(dueDate).toLocaleDateString()}</p>
                        )}
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 flex-shrink-0 mt-2" />
                    </Link>
                    {!isMandatory && (
                      <div className="flex items-center gap-1 px-4 pb-3 pl-[72px]">
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              await api.post(`/tasks/${task.task_id}/ignore`);
                              setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
                              setPendingCount((c) => Math.max(0, c - 1));
                            } catch { /* ignore errors */ }
                          }}
                          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 font-medium min-h-[32px] px-2 rounded hover:bg-gray-100 transition-colors"
                        >
                          <EyeOff className="w-3 h-3" />
                          Ignore
                        </button>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              await api.post(`/tasks/${task.task_id}/snooze`, { duration_days: 7 });
                              setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
                              setPendingCount((c) => Math.max(0, c - 1));
                            } catch { /* ignore errors */ }
                          }}
                          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 font-medium min-h-[32px] px-2 rounded hover:bg-gray-100 transition-colors"
                        >
                          <Clock className="w-3 h-3" />
                          Snooze 7 days
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
